import { KeenSliderOptions } from 'keen-slider'
import { useKeenSlider } from 'keen-slider/react'

import { useMemo, useState } from 'react'

import { GetShortVideoListViewModel } from '@/types/cml-api'

import { FETCH_NEXT_PAGE_THRESHOLD } from '../constants'

interface Params {
    fetchNextPage?: () => void
    shortsList: GetShortVideoListViewModel[]
    onSlideChange: (slug: string) => void
    shouldLoadNextPageInSwiper: boolean
    initialIndex: number
    initialVideoParam?: string
    shouldInsertInitialVideo: boolean
}

export const useVideosSlider = ({
    fetchNextPage,
    shortsList,
    onSlideChange,
    shouldLoadNextPageInSwiper,
    initialIndex,
    initialVideoParam,
    shouldInsertInitialVideo,
}: Params) => {
    const [isLoaded, setIsLoaded] = useState(false)
    const [activeIndex, setActiveIndex] = useState(initialIndex >= 0 ? initialIndex : 0)

    const config: KeenSliderOptions = useMemo(() => {
        return {
            initial: initialIndex === -1 ? 0 : initialIndex,
            loop: false,
            slides: {
                origin: 'center',
                perView: 1,
            },
            vertical: true,
            created: (slider) => {
                setIsLoaded(true)

                const sliderDetail = slider.track.details
                if (
                    fetchNextPage &&
                    shouldLoadNextPageInSwiper &&
                    sliderDetail.abs >= shortsList.length - FETCH_NEXT_PAGE_THRESHOLD
                ) {
                    fetchNextPage()
                }
            },
            slideChanged(slider) {
                if (!shortsList?.length) return

                const slideToIndex = slider.track.details.rel

                // check if user slide back to the initial video
                if (initialVideoParam && shouldInsertInitialVideo && slideToIndex === 0) {
                    onSlideChange(initialVideoParam)
                    setActiveIndex(0)

                    return
                }

                const currentSlug = shortsList?.[slideToIndex + (shouldInsertInitialVideo ? -1 : 0)]?.slug
                currentSlug && onSlideChange(currentSlug)
                setActiveIndex(slideToIndex)

                if (fetchNextPage && slideToIndex >= shortsList.length - FETCH_NEXT_PAGE_THRESHOLD) {
                    fetchNextPage()
                }
            },
        }
    }, [
        initialIndex,
        shouldLoadNextPageInSwiper,
        shortsList,
        fetchNextPage,
        initialVideoParam,
        shouldInsertInitialVideo,
        onSlideChange,
    ])

    const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>(config)

    return { config, isLoaded, activeIndex, sliderRef, instanceRef }
}
